/**
 * The id analysis and renumbering engine for `carcols.meta` / `carvariations.meta`.
 *
 * A mod kit, siren profile or light setting is a number claimed in one file and
 * trusted everywhere else. The competitor this replaces renames the *kit*
 * (rewriting `kitName` across two files) when all it needed to touch was the
 * number, and its scan reads only the first `<Kits>` block, treats a
 * commented-out `<Item>` as real, and never once checks whether an id is even in
 * range. Every one of those is a vehicle that looks fixed in the panel and
 * spawns broken in game, because the failure here is never a crash — a bad id
 * loads silently and shows up as "no mods" or "no lights" three sessions later.
 *
 * So this module answers one question honestly: is this id safe to change, and
 * if I change it, what else in this pack points at the old number and has to
 * change with it?
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE BINDING IS THE PRODUCT
 *
 * `carvariations.meta` binds a mod kit by *name* (`<kits><Item>police_modkit</Item>`)
 * and binds a siren or light profile by *number*
 * (`<sirenSettings value="700"/>`). Those are two different contracts and this
 * module treats them differently on purpose:
 *
 *   - A mod kit's id can be renumbered by touching exactly one file, at exactly
 *     one span, because nothing else in the pack ever names that number — every
 *     reference goes through the kit's name, which this module never rewrites.
 *     `planRenumber` never even looks at `carvariations.meta` for a kit move.
 *
 *   - A siren or light id has no name to hide behind. Renumbering the
 *     *definition* and leaving a `sirenSettings value="700"` pointed at the old
 *     number is exactly the dangling-binding failure this tool exists to
 *     prevent — the vehicle keeps compiling, keeps loading, and drives around
 *     with no lights. So a siren or light move ships with an edit for *every*
 *     reference this scan found, or it does not ship at all: see "REFUSING" below.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THE SCAN IS ITS OWN THING, NOT `parseCarcolsMeta`
 *
 * `vehicleMeta.ts` already parses these files, and its `itemsUnder` helper
 * reads exactly one container — the first `<Kits>` (or `<Sirens>`, or `<Lights>`)
 * it finds — because the vehicle analyzer only needs the vehicle's own record.
 * That is the wrong contract for a renumbering engine: a hand-merged
 * `carcols.meta` with two `<Kits>` blocks, or one written with lower-cased
 * section tags by a different tool, has every id in its second block silently
 * invisible to a scan built on that helper. So the container search here is its
 * own, case-insensitive, and gathers every matching container rather than the
 * first — see `descendantsNamed`.
 *
 * A `<!--` comment is not reparsed as a second copy of this problem: `DOMParser`
 * already never turns comment text into an `Element`, so a commented-out
 * `<Item>` simply is not in the tree this scan walks and never claims a slot in
 * an id pool. The one place a comment can still do damage is the *offset*
 * search that locates an id's exact span in the raw text for patching — a
 * hand-edited file that keeps an old, commented-out copy of a kit right next to
 * the live one can have identical digits sitting a few lines above the real
 * value, and a naive `indexOf` can find the dead copy first. So every offset
 * search here runs against a copy of the source with comments and CDATA bodies
 * blanked to spaces (`blankOutOfBandText`) rather than the original — same
 * length, same line numbers, and nothing inside a comment can ever be matched.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT "SAFE TO RENUMBER" MEANS HERE
 *
 * An id needs to move for one of two reasons, and they are handled differently:
 *
 *   - It collides with something (stock GTA, or another entry in this scan) or
 *     it is outright invalid (out of range, or the engine's sentinel for "none").
 *     Every entry that holds that id has to move — see `carcols.id.out_of_range`,
 *     `carcols.id.sentinel`, and the pool-specific duplicate/stock codes.
 *   - Two *otherwise fine* entries happen to share one id. The first one found
 *     keeps it; only the rest move, because there is nothing wrong with the
 *     number itself.
 *
 * The genuinely hard case is a siren or light id shared by two definitions where
 * *both* have to move (say, both are stock collisions). Every existing
 * `sirenSettings`/`lightSettings` reference to that number is now ambiguous —
 * it named one id that is about to become two different ids, and nothing in the
 * file says which vehicle meant which definition. `planRenumber` does not guess.
 * It refuses the whole group and says how many references it could not
 * disambiguate, in `RenumberPlan.refused`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * REFUSING
 *
 * A move is refused, rather than silently dropped, whenever any of these hold:
 *
 *   - *Any* resource in the scan has an unscannable `carvariations.meta`, for a
 *     siren or light id. This scan cannot prove it found every reference to the
 *     old number, and a move it cannot prove is a move it does not make. Any
 *     resource, not just the defining one, because a siren or light id is a
 *     global number: the reference that makes the move unsafe routinely lives in
 *     somebody else's resource, which is why the reference search spans the
 *     whole index in the first place.
 *   - The id's own definition span could not be relocated in the source text
 *     (should not happen for anything this scan itself produced, but a caller
 *     can hand back a stale `CarcolsIndex` against a file that has since
 *     changed, and this is the same "refuse, don't guess" contract `patch.ts`
 *     enforces at apply time).
 *   - The id pool this move needs has run out of safe ids to hand out.
 *   - Two or more definitions that share an id all have to move at once and at
 *     least one reference points at that shared id — the ambiguous case above.
 *
 * `RenumberPlan.moves` and `RenumberPlan.refused` between them account for
 * every offending id this scan found: nothing is quietly left off either list.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * SCOPE — what this deliberately does not do
 *
 * It does not read bytes and it does not write them. `indexCarcols` takes
 * already-decoded resources — the same `GtaResourceInput[]` every other
 * analyzer in this layer reads — and `planRenumber` hands back a plan of
 * `GtaTextEdit`s for the caller to run through `patch.ts`'s `applyEdits` (one
 * file at a time; `groupEditsByFile` does the grouping). The brief's own sketch
 * takes a raw `ResourceDrop`, but a `ResourceDrop`'s files are unread `File`
 * handles and decoding them is necessarily async, which nothing else pure in
 * this layer does; every other analyzer here is `GtaResourceInput` in, findings
 * out, and this module keeps that contract so a caller already holding
 * collected resources — which is every caller in this codebase — does not have
 * to decode a second time.
 *
 Its finding codes (`carcols.id.out_of_range` and its siblings) are members of
 * `GtaFindingCode` in `types.ts`, so `carcolsFindings` returns plain
 * `GtaFinding[]` and every shared consumer — `sortFindings`, `groupByEntity`,
 * `FindingsList`, `report.ts` — takes them with no cast.
 *
 * `GtaEntityKind` is in the same position: `siren` and `light`
 * are members of it, and `ENTITY_KIND` below stamps each pool's findings with
 * its own. That had to change rather than stay deferred, because
 * `groupByEntity` keys on `kind + id` and the previous blanket `"modkit"` made
 * an unrelated siren collision render inside a mod kit's group, under a mod
 * kit's heading, whenever the two happened to share a number — which for ids
 * under 255 is ordinary.
 *
 * It does not know about `handling.meta`, `vehicles.meta` or anything outside
 * `carcols.meta` / `carvariations.meta`. A vehicle whose `kitName` names nothing
 * this scan found is `carcols.kit.dangling`; everything else about that vehicle
 * is `vehicleMeta.ts`'s job.
 *
 * The offset search that locates each id's span shares one forward-moving
 * cursor per file, advanced in the order this scan visits things — every
 * `<Kits>` block, then every `<Sirens>` block, then every `<Lights>` block,
 * matching the order Rockstar's own files always use. A file that interleaves
 * them in some other order (a `<Sirens>` block physically sitting between two
 * `<Kits>` blocks, say) can make a search start further forward than that
 * section's real position, which either finds nothing — and the id is treated
 * as un-relocatable, refused rather than guessed at, same as any other stale
 * span — or, rarely, a later coincidental match. This is the same forward-
 * cursor tradeoff `vehicleMeta.ts` already accepts for the same reason: capping
 * every search to a hand-computed per-item window would need this scan to know
 * where an `<Item>` ends, which the DOM it is built from does not expose.
 *
 * It never assumes the resource root. It reads whatever
 * `GtaResourceInput.texts` hands it, by filename, wherever that path sits — a
 * `carcols.meta` at the resource root and a `carvariations.meta` under `data/`
 * are simply two entries in the same map. Resolving *which* directory is one
 * resource happened upstream, in `intake.ts`, before this module ever saw the
 * input; this module never assumes a fixed sibling layout the way a
 * parent-directory split would.
 */

import { isEmergencyVehicleName } from "./data/emergencyVehicles.v1";
import { SIREN_BONE_LIMIT, SIREN_BONE_LIMIT_WITH_SSLA, sirenCountVerdict } from "./data/engineLimits.v1";
import { VANILLA_LIGHT_IDS, VANILLA_SIREN_IDS } from "./data/vanillaModkits.v1";
import {
  allocateIds,
  ID_POOLS,
  isDefaultModkitName,
  isStockModkitId,
  isStockModkitName,
  stockKitsForId,
  type IdPool,
} from "./modkits";
import { normalizeName, pathKey, positionAt } from "./normalize";
import { textEdit } from "./patch";
import type {
  GtaEntity,
  GtaFinding,
  GtaFindingCode,
  GtaResourceInput,
  GtaSourceSpan,
  GtaTextEdit,
} from "./types";
import { parseMetaXml } from "./vehicleMeta";

// ─── Limits ──────────────────────────────────────────────────────────────────

/** Carcols/carvariations files read per resource. A folder past this is not a resource. */
const MAX_FILES_PER_RESOURCE = 200;

/** Containers (`<Kits>`, `<Sirens>`, `<Lights>`, `<variationData>`) read per file. */
const MAX_CONTAINERS_PER_FILE = 64;

/** `<Item>` children read per container. Real files hold dozens, not thousands. */
const MAX_ITEMS_PER_CONTAINER = 20_000;

/** Ids one `planRenumber` call will allocate. Mirrors `modkits.ts`'s own batch cap. */
const MAX_RENUMBER_BATCH = 512;

// ─── The index ───────────────────────────────────────────────────────────────

/** A mod kit `carcols.meta` defines, with the id's own span for patching. */
export type CarcolsKit = {
  id: number;
  /** The id exactly as written — what an edit's `expect` has to match. */
  raw: string;
  kitName: string;
  span: GtaSourceSpan;
  file: string;
  /** Which `GtaResourceInput` this came from: `identity ?? name`. */
  resource: string;
};

/** A siren or light profile `carcols.meta` defines. */
export type CarcolsProfile = {
  kind: "siren" | "light";
  id: number;
  raw: string;
  span: GtaSourceSpan;
  file: string;
  resource: string;
  /** Siren only: `<Item>` count under the profile's nested `<sirens>` list. */
  boneCount?: number;
};

/** One thing a `carvariations.meta` `<Item>` claims about a vehicle. */
export type CarcolsVariationRef = {
  kind: "kit" | "siren" | "light";
  /** The kit name, or the numeric id, exactly as written. */
  raw: string;
  /** Parsed id for `siren`/`light`; always `null` for `kit`, which is name-keyed. */
  id: number | null;
  /** The vehicle's `modelName`, `""` when the item did not state one. */
  model: string;
  span: GtaSourceSpan;
  file: string;
  resource: string;
};

/** A file, or a section of one, this scan could not read. Never silent. */
export type CarcolsUnscannable = { file: string; resource: string; reason: string };

export type CarcolsIndex = {
  kits: CarcolsKit[];
  sirens: CarcolsProfile[];
  lights: CarcolsProfile[];
  /** `carvariations.meta` references, kept for the dangling-kit check and for renumbering. */
  variationRefs: CarcolsVariationRef[];
  unscannable: CarcolsUnscannable[];
};

// ─── XML helpers — case-insensitive, multi-container, comment-safe ──────────

/** `\`` `.` `*` and friends escaped, so a raw kit name can sit inside a `RegExp`. */
function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Comment and CDATA bodies replaced with spaces of the same length, newlines kept. */
const COMMENT_OR_CDATA = /<!--[\s\S]*?-->|<!\[CDATA\[[\s\S]*?\]\]>/g;
function blankOutOfBandText(source: string): string {
  return source.replace(COMMENT_OR_CDATA, (match) => match.replace(/[^\n]/g, " "));
}

function localName(el: Element): string {
  return el.tagName.toLowerCase();
}

/** Direct children of `parent` named `tag`, matched without regard to case. */
function childrenNamed(parent: ParentNode, tag: string): Element[] {
  const lower = tag.toLowerCase();
  const out: Element[] = [];
  const children = "children" in parent ? Array.from((parent as Element | Document).children) : [];
  for (const child of children) {
    if (localName(child) === lower) out.push(child);
    if (out.length >= MAX_ITEMS_PER_CONTAINER) break;
  }
  return out;
}

function firstChildNamed(parent: ParentNode, tag: string): Element | null {
  return childrenNamed(parent, tag)[0] ?? null;
}

/**
 * Every element named `tag` anywhere under `scope`, case-insensitively.
 *
 * Deliberately not `querySelector` — this is what makes two `<Kits>` blocks and
 * a lower-cased `<kits>` both visible, where `itemsUnder`'s single first-match
 * lookup would drop everything after (or spelled unlike) the first one it saw.
 */
function descendantsNamed(scope: Document, tag: string): Element[] {
  const lower = tag.toLowerCase();
  const out: Element[] = [];
  const all = scope.querySelectorAll("*");
  for (const el of Array.from(all)) {
    if (localName(el) === lower) out.push(el);
    if (out.length >= MAX_CONTAINERS_PER_FILE) break;
  }
  return out;
}

/** An element's `value` attribute or text, trimmed; `null` for absent or blank. */
function valueOf(el: Element | null): string | null {
  if (!el) return null;
  const raw = (el.getAttribute("value") ?? el.textContent ?? "").trim();
  return raw === "" ? null : raw;
}

function childValue(parent: Element, tag: string): string | null {
  return valueOf(firstChildNamed(parent, tag));
}

/** Whole-string integers only — `"16abc"` is not id 16, it is unreadable. */
function wholeInt(raw: string | null): number | null {
  if (raw === null || !/^-?\d+$/.test(raw)) return null;
  return Number.parseInt(raw, 10);
}

/**
 * Where a tag's value sits in `source`, searching forward from `from`.
 *
 * Case-insensitive and tolerant of both spellings a `.meta` writes a scalar in —
 * `<tag>value</tag>` and self-closing `<tag value="value"/>` — matching the
 * pattern `vehicleMeta.ts`'s own `tagOffset` uses, so a hand-tabbed file with
 * mixed styles patches the same way in both places. Returns `-1` when the value
 * cannot be found, and the caller then refuses rather than guesses a span.
 */
function tagOffset(source: string, tag: string, value: string, from = 0): number {
  if (value === "") return -1;
  const name = escapeRegExp(tag);
  const text = escapeRegExp(value);
  const patterns = [
    new RegExp(`(<${name}\\b[^>]*>\\s*)${text}\\s*</`, "i"),
    new RegExp(`(<${name}\\b[^>]*value\\s*=\\s*["'])${text}["']`, "i"),
  ];
  const start = Math.max(0, from);
  const window = source.slice(start);
  for (const pattern of patterns) {
    const match = pattern.exec(window);
    if (match) return start + match.index + match[1].length;
  }
  return -1;
}

function spanAt(file: string, source: string, offset: number, length: number): GtaSourceSpan {
  if (offset < 0) return { file };
  const { line, column } = positionAt(source, offset);
  return { file, line, column, byteStart: offset, byteEnd: offset + length };
}

// ─── Which files this scan reads ────────────────────────────────────────────

const CARCOLS_FILE = /(?:^|\/)carcols\.meta$/i;
const CARVARIATIONS_FILE = /(?:^|\/)carvariations\.meta$/i;

/**
 * Every resource in the scan holding a `carvariations.meta` this scan could not
 * read, in first-seen order.
 *
 * Across the whole index, not per resource, and that is the correction: a siren
 * or light id is a *global* number with no name to hide behind, so the
 * `<sirenSettings value="5"/>` that makes a move unsafe can legitimately live in
 * a completely different resource from the `<Sirens>` block that defines id 5 —
 * `planRenumber` already searches every resource's references for exactly that
 * reason. Asking only the *defining* resource whether it had an unreadable
 * carvariations.meta answered a narrower question than the one that matters, and
 * answered it reassuringly: an unrelated resource with a corrupt
 * carvariations.meta let the move through, even though that file could have held
 * the one reference nobody would ever fix.
 */
function unscannableCarvariationResources(index: Pick<CarcolsIndex, "unscannable">): string[] {
  const out: string[] = [];
  for (const entry of index.unscannable) {
    if (!CARVARIATIONS_FILE.test(entry.file)) continue;
    if (!out.includes(entry.resource)) out.push(entry.resource);
  }
  return out;
}

// ─── Scanning ────────────────────────────────────────────────────────────────

/**
 * Read every `carcols.meta` and `carvariations.meta` across a set of already
 * -collected resources.
 *
 * Nothing here reads bytes: `resources` is the same `GtaResourceInput[]` every
 * other analyzer in this layer takes, with `.texts` already decoded by
 * `collectResources`. See the module header for why this differs from the
 * brief's raw-`ResourceDrop` sketch.
 */
export function indexCarcols(resources: readonly GtaResourceInput[]): CarcolsIndex {
  const kits: CarcolsKit[] = [];
  const sirens: CarcolsProfile[] = [];
  const lights: CarcolsProfile[] = [];
  const variationRefs: CarcolsVariationRef[] = [];
  const unscannable: CarcolsUnscannable[] = [];

  for (const input of resources) {
    const resourceName = input.identity ?? input.name;
    let filesScanned = 0;

    for (const [path, text] of input.texts) {
      const isCarcols = CARCOLS_FILE.test(path);
      const isCarvariations = !isCarcols && CARVARIATIONS_FILE.test(path);
      if (!isCarcols && !isCarvariations) continue;

      if (filesScanned >= MAX_FILES_PER_RESOURCE) {
        unscannable.push({
          file: path,
          resource: resourceName,
          reason:
            `This resource has more than ${MAX_FILES_PER_RESOURCE} carcols/carvariations ` +
            "files; this one was not scanned, and nothing in it was indexed or checked.",
        });
        continue;
      }
      filesScanned += 1;

      const doc = parseMetaXml(text);
      if (!doc) {
        unscannable.push({
          file: path,
          resource: resourceName,
          reason:
            "This file does not parse as well-formed XML, so nothing in it could be read. " +
            "It was left untouched — patching a file this scan could not verify is how a " +
            "generator turns one broken file into two.",
        });
        continue;
      }

      const stripped = blankOutOfBandText(text);

      if (isCarcols) {
        scanCarcols(doc, path, text, stripped, resourceName, kits, sirens, lights, unscannable);
      } else {
        scanCarvariations(doc, path, text, stripped, resourceName, variationRefs);
      }
    }
  }

  return { kits, sirens, lights, variationRefs, unscannable };
}

function scanCarcols(
  doc: Document,
  file: string,
  source: string,
  stripped: string,
  resource: string,
  kits: CarcolsKit[],
  sirens: CarcolsProfile[],
  lights: CarcolsProfile[],
  unscannable: CarcolsUnscannable[],
): void {
  let cursor = 0;

  for (const container of descendantsNamed(doc, "Kits")) {
    for (const item of childrenNamed(container, "Item")) {
      const kitName = childValue(item, "kitName") ?? "";
      const rawId = childValue(item, "id");
      const id = wholeInt(rawId);
      if (id === null) {
        unscannable.push({
          file,
          resource,
          reason:
            `A <Kits> item${kitName ? ` (kitName "${kitName}")` : ""} has no readable whole` +
            "-number <id>, so it was not indexed and cannot be checked or renumbered.",
        });
        continue;
      }
      const offset = tagOffset(stripped, "id", rawId as string, cursor);
      if (offset >= 0) cursor = offset + (rawId as string).length;
      kits.push({
        id,
        raw: rawId as string,
        kitName,
        span: spanAt(file, source, offset, (rawId as string).length),
        file,
        resource,
      });
    }
  }

  for (const container of descendantsNamed(doc, "Sirens")) {
    for (const item of childrenNamed(container, "Item")) {
      const rawId = childValue(item, "id");
      const id = wholeInt(rawId);
      if (id === null) {
        unscannable.push({
          file,
          resource,
          reason: "A <Sirens> item has no readable whole-number <id>, so it was not indexed.",
        });
        continue;
      }
      const offset = tagOffset(stripped, "id", rawId as string, cursor);
      if (offset >= 0) cursor = offset + (rawId as string).length;
      const bones = firstChildNamed(item, "sirens");
      const boneCount = bones ? childrenNamed(bones, "Item").length : undefined;
      sirens.push({
        kind: "siren",
        id,
        raw: rawId as string,
        span: spanAt(file, source, offset, (rawId as string).length),
        file,
        resource,
        boneCount,
      });
    }
  }

  for (const container of descendantsNamed(doc, "Lights")) {
    for (const item of childrenNamed(container, "Item")) {
      const rawId = childValue(item, "id");
      const id = wholeInt(rawId);
      if (id === null) {
        unscannable.push({
          file,
          resource,
          reason: "A <Lights> item has no readable whole-number <id>, so it was not indexed.",
        });
        continue;
      }
      const offset = tagOffset(stripped, "id", rawId as string, cursor);
      if (offset >= 0) cursor = offset + (rawId as string).length;
      lights.push({
        kind: "light",
        id,
        raw: rawId as string,
        span: spanAt(file, source, offset, (rawId as string).length),
        file,
        resource,
      });
    }
  }
}

function scanCarvariations(
  doc: Document,
  file: string,
  source: string,
  stripped: string,
  resource: string,
  variationRefs: CarcolsVariationRef[],
): void {
  let cursor = 0;

  for (const container of descendantsNamed(doc, "variationData")) {
    for (const item of childrenNamed(container, "Item")) {
      const model = normalizeName(childValue(item, "modelName") ?? "");
      const anchorSource = childValue(item, "modelName") ?? "";
      const modelOffset = anchorSource !== "" ? tagOffset(stripped, "modelName", anchorSource, cursor) : -1;
      const anchor = modelOffset >= 0 ? modelOffset : cursor;
      let forward = anchor;

      const kitsContainer = firstChildNamed(item, "kits");
      if (kitsContainer) {
        for (const kitItem of childrenNamed(kitsContainer, "Item")) {
          const kitName = valueOf(kitItem) ?? "";
          if (kitName === "") continue;
          const offset = tagOffset(stripped, "Item", kitName, forward);
          if (offset >= 0) forward = offset + kitName.length;
          variationRefs.push({
            kind: "kit",
            raw: kitName,
            id: null,
            model,
            span: spanAt(file, source, offset, kitName.length),
            file,
            resource,
          });
        }
      }

      const sirenRaw = childValue(item, "sirenSettings");
      if (sirenRaw !== null) {
        const offset = tagOffset(stripped, "sirenSettings", sirenRaw, anchor);
        if (offset >= 0) forward = Math.max(forward, offset + sirenRaw.length);
        variationRefs.push({
          kind: "siren",
          raw: sirenRaw,
          id: wholeInt(sirenRaw),
          model,
          span: spanAt(file, source, offset, sirenRaw.length),
          file,
          resource,
        });
      }

      const lightRaw = childValue(item, "lightSettings");
      if (lightRaw !== null) {
        const offset = tagOffset(stripped, "lightSettings", lightRaw, anchor);
        if (offset >= 0) forward = Math.max(forward, offset + lightRaw.length);
        variationRefs.push({
          kind: "light",
          raw: lightRaw,
          id: wholeInt(lightRaw),
          model,
          span: spanAt(file, source, offset, lightRaw.length),
          file,
          resource,
        });
      }

      cursor = Math.max(cursor, forward);
    }
  }
}

// ─── Findings ────────────────────────────────────────────────────────────────

export type CarcolsFindingsOptions = {
  /** The server runs the Siren Setting Limit Adjuster. Default `false`. */
  ssla?: boolean;
};

function rangePool(kind: "modkit" | "siren" | "light", ssla: boolean): IdPool {
  if (kind === "modkit") return "modkit";
  if (kind === "siren") return ssla ? "sirenSsla" : "siren";
  return "light";
}

/** `null` when the id is fine; otherwise which of the two range codes fires and why. */
function rangeProblem(
  kind: "modkit" | "siren" | "light",
  id: number,
  ssla: boolean,
): { code: "carcols.id.out_of_range" | "carcols.id.sentinel"; detail: string } | null {
  const spec = ID_POOLS[rangePool(kind, ssla)];
  if (!Number.isInteger(id) || id < spec.min || id > spec.max) {
    return {
      code: "carcols.id.out_of_range",
      detail:
        `The usable range for a ${spec.label} id is ${spec.min}-${spec.max}` +
        (kind === "siren" ? (ssla ? " with the Siren Setting Limit Adjuster" : " on a stock server") : "") +
        `, and this file has ${id}. The engine cannot store a value outside that range, so this ` +
        "vehicle loads with no working " + kind + " at all.",
    };
  }
  if (spec.sentinels.includes(id)) {
    return {
      code: "carcols.id.sentinel",
      detail:
        `${id} is the value the engine reads as "no ${kind}" for this field, so a vehicle` +
        ` assigned it behaves as though it had none, even though the file states an id.`,
    };
  }
  return null;
}

/**
 * The `GtaEntity` kind each id pool's findings are stamped with.
 *
 * One entry per pool, and not the identity mapping it looks like — the point is
 * that it is not a constant. Every finding here used to carry
 * `kind: "modkit"` regardless of pool, because `GtaEntityKind` had no `siren` or
 * `light` member, and `groupByEntity` keys a group on `kind + id`: a mod kit id
 * 100 and an unrelated siren profile 100 merged into one group, rendering the
 * siren's problem under a heading reading "Mod kit 100" and counting two broken
 * entities as one. Siren ids run 1–254, so sharing a number with a low custom
 * kit id is ordinary rather than a coincidence. The kinds now exist; this is the
 * table that uses them.
 */
const ENTITY_KIND = {
  modkit: "modkit",
  siren: "siren",
  light: "light",
} as const satisfies Record<"modkit" | "siren" | "light", GtaEntity["kind"]>;

function isStockId(kind: "modkit" | "siren" | "light", id: number): boolean {
  if (kind === "modkit") return isStockModkitId(id);
  if (kind === "siren") return VANILLA_SIREN_IDS.includes(id);
  return VANILLA_LIGHT_IDS.includes(id);
}

function stockOwnerNote(kind: "modkit" | "siren" | "light", id: number): string {
  if (kind !== "modkit") return "";
  const kits = stockKitsForId(id).map((kit) => kit[1]);
  return kits.length > 0 ? ` (${kits.join(", ")})` : "";
}

/** First appearance first, so "which one keeps the id" reads left to right. */
function groupById<T extends { id: number }>(entries: readonly T[]): Map<number, T[]> {
  const map = new Map<number, T[]>();
  for (const entry of entries) {
    const list = map.get(entry.id);
    if (list) list.push(entry);
    else map.set(entry.id, [entry]);
  }
  return map;
}

function emergencyModelsFor(
  kind: "kit" | "siren" | "light",
  key: string | number,
  refs: readonly CarcolsVariationRef[],
): string[] {
  const models = new Set<string>();
  for (const ref of refs) {
    if (ref.kind !== kind) continue;
    const matches = kind === "kit" ? normalizeName(ref.raw) === normalizeName(String(key)) : ref.id === key;
    if (matches && ref.model !== "" && isEmergencyVehicleName(ref.model)) models.add(ref.model);
  }
  return [...models];
}

/**
 * Every id-shaped problem in a `CarcolsIndex`, plus the sections this scan
 * could not read at all.
 *
 * Duplicate and stock-collision findings fire once per offending id — not once
 * per entry — because "A, B and C all claim id 700" is one problem with three
 * culprits, and three separate findings that only differ by which name they
 * lead with is the kind of noise that makes a report unreadable.
 */
export function carcolsFindings(
  index: CarcolsIndex,
  options: CarcolsFindingsOptions = {},
): GtaFinding[] {
  const ssla = options.ssla === true;
  const findings: GtaFinding[] = [];

  for (const entry of index.unscannable) {
    findings.push({
      code: "carcols.xml.unscannable",
      severity: "warning",
      title: `${entry.file} could not be scanned`,
      detail: entry.reason,
      file: entry.file,
      entity: { kind: "resource", id: entry.resource },
    });
  }

  const pools: { kind: "modkit" | "siren" | "light"; entries: (CarcolsKit | CarcolsProfile)[] }[] = [
    { kind: "modkit", entries: index.kits },
    { kind: "siren", entries: index.sirens },
    { kind: "light", entries: index.lights },
  ];

  for (const { kind, entries } of pools) {
    const groups = groupById(entries);
    for (const [id, group] of groups) {
      const problem = rangeProblem(kind, id, ssla);
      const stock = !problem && isStockId(kind, id);

      const emergency = group.flatMap((entry) =>
        emergencyModelsFor(
          kind === "modkit" ? "kit" : kind,
          kind === "modkit" && "kitName" in entry ? entry.kitName : id,
          index.variationRefs,
        ),
      );
      const tags = emergency.length > 0 ? (["emergency-impact"] as const) : undefined;

      if (problem) {
        for (const entry of group) {
          findings.push({
            code: problem.code,
            severity: "error",
            title: `${kind === "modkit" ? "Mod kit" : kind === "siren" ? "Siren" : "Light"} id ${id} is invalid`,
            detail: problem.detail,
            ...entry.span,
            entity: { kind: ENTITY_KIND[kind], id: String(id) },
            tags: tags ? [...tags] : undefined,
          });
        }
        continue;
      }

      if (stock) {
        const owner = stockOwnerNote(kind, id);
        const code: GtaFindingCode =
          kind === "modkit"
            ? "carcols.modkit.stock_id"
            : kind === "siren"
              ? "carcols.siren.stock_id"
              : "carcols.light.stock_id";
        for (const entry of group) {
          findings.push({
            code,
            severity: "error",
            title: `${kind === "modkit" ? "Mod kit" : kind === "siren" ? "Siren" : "Light"} id ${id} belongs to stock GTA${owner}`,
            detail:
              `Stock GTA already uses ${kind} id ${id}${owner}. A custom entry claiming the same ` +
              "number overwrites the base game's, and the stock vehicles built on it inherit " +
              "whatever this pack defines instead of their own.",
            ...entry.span,
            entity: { kind: ENTITY_KIND[kind], id: String(id) },
            tags: tags ? [...tags] : undefined,
          });
        }
        continue;
      }

      if (group.length > 1) {
        const names =
          kind === "modkit"
            ? group.map((entry) => ("kitName" in entry ? entry.kitName : "")).join(", ")
            : group.map((entry) => entry.file).join(", ");
        const code: GtaFindingCode =
          kind === "modkit"
            ? "carcols.modkit.duplicate_id"
            : kind === "siren"
              ? "carcols.siren.duplicate_id"
              : "carcols.light.duplicate_id";
        findings.push({
          code,
          severity: "error",
          title: `${kind === "modkit" ? "Mod kit" : kind === "siren" ? "Siren" : "Light"} id ${id} is claimed ${group.length} times`,
          detail:
            `${names} all claim ${kind} id ${id}. The engine indexes this field by number, so ` +
            "only one of these definitions is ever read — which one depends on load order, and " +
            "the rest silently lose whatever they defined.",
          ...group[0].span,
          entity: { kind: ENTITY_KIND[kind], id: String(id) },
          tags: tags ? [...tags] : undefined,
        });
      }
    }
  }

  const carsWithUnscannableCarcols = new Set(
    index.unscannable.filter((entry) => CARCOLS_FILE.test(entry.file)).map((entry) => entry.resource),
  );
  const kitsByName = new Map<string, CarcolsKit>();
  for (const kit of index.kits) {
    const key = normalizeName(kit.kitName);
    if (!kitsByName.has(key)) kitsByName.set(key, kit);
  }
  for (const ref of index.variationRefs) {
    if (ref.kind !== "kit") continue;
    if (isDefaultModkitName(ref.raw) || isStockModkitName(ref.raw)) continue;
    if (kitsByName.has(normalizeName(ref.raw))) continue;
    if (carsWithUnscannableCarcols.has(ref.resource)) continue; // honesty: might exist, unreadably
    findings.push({
      code: "carcols.kit.dangling",
      severity: "error",
      title: `Mod kit "${ref.raw}" is not defined anywhere this scan could read`,
      detail:
        `${ref.model || "This vehicle"} names mod kit "${ref.raw}" in carvariations.meta, but no ` +
        "carcols.meta this scan read defines a kit with that name. The vehicle spawns with no " +
        "mods at all in LSC, and no error is ever shown for it.",
      ...ref.span,
      entity: { kind: "vehicle", id: ref.model || ref.raw },
      tags: ref.model !== "" && isEmergencyVehicleName(ref.model) ? ["emergency-impact"] : undefined,
    });
  }

  // ── A siren/light reference pointing at a number nothing defines ──────────
  //
  // `carcols.kit.dangling` above is the name-keyed sibling of this check, and
  // for a long time it was the only one — a `<sirenSettings value="12345"/>`
  // with no `<Sirens>` item numbered 12345 anywhere in the scan produced no
  // finding at all. That is the same failure the module header calls "exactly
  // the dangling-binding failure this tool exists to prevent" when it is talking
  // about renumber cascades, and a reference that arrived broken is no less
  // broken than one a bad cascade created. It is also the check that makes the
  // *after* scan mean something: a write path that leaves a reference dangling
  // can only be caught by a scan that knows how to see one.
  //
  // Scoped across the whole index rather than per resource, because a siren or
  // light id is global: the definition legitimately lives in another resource's
  // carcols.meta. That is also why the honesty guard is global — if ANY resource
  // in the scan has an unreadable carcols.meta, the definition might be sitting
  // in it, so nothing is called dangling.
  //
  // A stock id and a sentinel are both exempt; see the two guards in the loop.
  // Getting that wrong is how a check like this becomes noise rather than
  // signal, and it is exactly what a client test caught while this was being
  // written: `<lightSettings value="0"/>` appears in nearly every
  // carvariations.meta in existence, and 0 is a base-game light setting.
  const anyUnscannableCarcols = index.unscannable.some((entry) => CARCOLS_FILE.test(entry.file));
  if (!anyUnscannableCarcols) {
    const definedIds = {
      siren: new Set(index.sirens.map((profile) => profile.id)),
      light: new Set(index.lights.map((profile) => profile.id)),
    };
    for (const ref of index.variationRefs) {
      if (ref.kind === "kit") continue;
      if (ref.id === null) continue;
      if (definedIds[ref.kind].has(ref.id)) continue;
      // The two exemptions the kit-dangling check has as
      // `isDefaultModkitName`/`isStockModkitName`, in numeric form.
      //
      // A stock id is defined by the base game's own carcols.meta, which is
      // never in a user's drop and never will be, so "nothing in this scan
      // defines it" says nothing at all about it. Without this, every ordinary
      // `<lightSettings value="0"/>` — which is most rows in most
      // carvariations.meta files ever written — would be reported as a broken
      // reference. A check that fires on the normal case is worse than no check.
      if (isStockId(ref.kind, ref.id)) continue;
      // A sentinel is the engine's own "this vehicle has none", not a pointer
      // at a definition, so there is nothing for it to dangle from.
      if (ID_POOLS[rangePool(ref.kind, ssla)].sentinels.includes(ref.id)) continue;
      const label = ref.kind === "siren" ? "Siren" : "Light";
      const field = ref.kind === "siren" ? "sirenSettings" : "lightSettings";
      findings.push({
        code: ref.kind === "siren" ? "carcols.siren.dangling" : "carcols.light.dangling",
        severity: "error",
        title: `${label} id ${ref.id} is not defined anywhere this scan could read`,
        detail:
          `${ref.model || "This vehicle"} points <${field}> at ${ref.kind} id ${ref.id}, but no ` +
          "carcols.meta in this scan defines one with that number. The engine looks this field up " +
          `by number and finds nothing, so the vehicle spawns with no ${ref.kind} behaviour at ` +
          "all — and never logs an error for it. Either the carcols.meta that defines it was not " +
          "included in this drop, or the number is a typo.",
        ...ref.span,
        entity: { kind: ENTITY_KIND[ref.kind], id: String(ref.id) },
        tags: ref.model !== "" && isEmergencyVehicleName(ref.model) ? ["emergency-impact"] : undefined,
      });
    }
  }

  for (const siren of index.sirens) {
    if (siren.boneCount === undefined) continue;
    const verdict = sirenCountVerdict(siren.boneCount, { ssla });
    if (!verdict) continue;
    const models = emergencyModelsFor("siren", siren.id, index.variationRefs);
    findings.push({
      code: "carcols.siren.over_limit",
      severity: "warning",
      title: `Siren profile ${siren.id} has more sirens than a ${verdict.needsSsla ? "stock" : "supported"} server can drive`,
      detail:
        `This profile lists ${siren.boneCount} sirens. The base engine drives sirens ` +
        `siren1..siren${SIREN_BONE_LIMIT}, so anything past ${SIREN_BONE_LIMIT} has no bone to ` +
        "attach to on a stock server." +
        (verdict.needsSsla
          ? ` A server running the Siren Setting Limit Adjuster raises that to ` +
            `${SIREN_BONE_LIMIT_WITH_SSLA}, which does cover this profile — but a player on a ` +
            "server without it sees the extras do nothing."
          : ` Even the Siren Setting Limit Adjuster only raises that to ` +
            `${SIREN_BONE_LIMIT_WITH_SSLA}, which this profile also exceeds by ` +
            `${verdict.over}.`),
      ...siren.span,
      entity: { kind: "siren", id: String(siren.id) },
      tags: models.length > 0 ? ["emergency-impact"] : undefined,
    });
  }

  return findings;
}

// ─── Renumbering ─────────────────────────────────────────────────────────────

export type RenumberOptions = {
  /** The server runs the Siren Setting Limit Adjuster. Default `false`. */
  ssla?: boolean;
};

/**
 * One edit, carrying the resource whose file it belongs to.
 *
 * The resource tag is not redundant with `RenumberMove.resource`, and assuming
 * it was is how this engine wrote a false success. A siren or light id is
 * global, so a single move's edits can legitimately span two resources: the
 * definition in resource A's `carcols.meta`, the cascade in resource B's
 * `carvariations.meta`. `GtaTextEdit.file` is a resource-*relative* path, and
 * `carvariations.meta` is the near-universal spelling, so a caller that looked
 * every edit up in `move.resource`'s own texts checked B's edit against A's
 * same-named file, watched `applyEdits` reject it on the text mismatch, and —
 * because a file where nothing applied is skipped — never surfaced the
 * rejection anywhere. The definition moved, the real reference stayed pointed at
 * an id that no longer exists, and the after-scan read clean.
 */
export type RenumberEdit = {
  /** Which resource's file this edit belongs to — `identity ?? name`. */
  resource: string;
  edit: GtaTextEdit;
};

export type RenumberMove = {
  pool: IdPool;
  /** The resource holding the *definition* being renumbered. See `edits`. */
  resource: string;
  from: number;
  to: number;
  /** Present for modkit moves, so a UI can show the name that stays put. */
  kitName?: string;
  reason: string;
  /**
   * Every edit this move needs — the definition, plus any reference this pool
   * cascades to. Each carries its own resource, which is not always
   * `move.resource`: see `RenumberEdit`.
   */
  edits: RenumberEdit[];
};

export type RenumberRefusal = {
  pool: IdPool;
  resource: string;
  id: number;
  reason: string;
};

export type RenumberPlan = {
  moves: RenumberMove[];
  refused: RenumberRefusal[];
};

type Mover<T> = { entry: T; reason: string };

/** Which entries in one id-group must move, and why. Never mutates `group`. */
function moversFor<T extends { id: number }>(
  group: readonly T[],
  kind: "modkit" | "siren" | "light",
  ssla: boolean,
): Mover<T>[] {
  const problem = rangeProblem(kind, group[0].id, ssla);
  const stock = !problem && isStockId(kind, group[0].id);

  if (problem) {
    return group.map((entry) => ({ entry, reason: problem.detail }));
  }
  if (stock) {
    return group.map((entry) => ({
      entry,
      reason: `Id ${group[0].id} belongs to stock GTA${stockOwnerNote(kind, group[0].id)}.`,
    }));
  }
  if (group.length > 1) {
    // The id itself is fine; only the extras beyond the first mover.
    return group.slice(1).map((entry) => ({
      entry,
      reason: `Id ${group[0].id} is claimed by more than one entry; this is not the first.`,
    }));
  }
  return [];
}

function definitionEdit(
  resource: string,
  file: string,
  span: GtaSourceSpan,
  raw: string,
  to: number,
): RenumberEdit | null {
  if (typeof span.byteStart !== "number" || typeof span.byteEnd !== "number") return null;
  return { resource, edit: textEdit(file, span.byteStart, span.byteEnd, raw, String(to)) };
}

function referenceEdit(ref: CarcolsVariationRef, to: number): RenumberEdit | null {
  if (typeof ref.span.byteStart !== "number" || typeof ref.span.byteEnd !== "number") return null;
  return {
    // `ref.resource`, never the mover's — this is the whole point of the tag.
    resource: ref.resource,
    edit: textEdit(ref.file, ref.span.byteStart, ref.span.byteEnd, ref.raw, String(to)),
  };
}

/**
 * A complete renumbering plan for every offending id in `index`.
 *
 * "Complete" means every id `carcolsFindings` would call invalid, stock or
 * duplicate is accounted for in exactly one of `moves` or `refused` — there is
 * no third, silent outcome. Nothing here writes a byte; run the edits in
 * `moves` through `patch.ts`'s `applyEdits`, one file at a time (see
 * `groupEditsByFile`).
 */
export function planRenumber(index: CarcolsIndex, options: RenumberOptions = {}): RenumberPlan {
  const ssla = options.ssla === true;
  const moves: RenumberMove[] = [];
  const refused: RenumberRefusal[] = [];
  /** Computed once: it is a property of the whole scan, not of any one move. */
  const unverifiableCarvariations = unscannableCarvariationResources(index);

  // ── Mod kits: name-bound, so a move never touches carvariations.meta. ──────

  {
    const groups = groupById(index.kits);
    const allMovers = [...groups.values()].flatMap((group) => moversFor(group, "modkit", ssla));
    const taken = new Set(index.kits.map((kit) => kit.id));
    const newIds = allocateIds(
      "modkit",
      Math.min(allMovers.length, MAX_RENUMBER_BATCH),
      taken,
    );
    let cursor = 0;
    for (const { entry, reason } of allMovers) {
      if (cursor >= newIds.length) {
        refused.push({
          pool: "modkit",
          resource: entry.resource,
          id: entry.id,
          reason:
            "The mod kit id pool has no more safe ids left to hand out (every id under 65535 " +
            "is either stock GTA's, a sentinel, or already claimed in this scan), so this kit " +
            "was left at its current id.",
        });
        continue;
      }
      const to = newIds[cursor];
      cursor += 1;
      const edit = definitionEdit(entry.resource, entry.file, entry.span, entry.raw, to);
      if (!edit) {
        refused.push({
          pool: "modkit",
          resource: entry.resource,
          id: entry.id,
          reason:
            `The exact text of kit "${entry.kitName}"'s id could not be relocated in ` +
            `${entry.file}, so no edit could be built for it safely.`,
        });
        continue;
      }
      moves.push({
        pool: "modkit",
        resource: entry.resource,
        from: entry.id,
        to,
        kitName: entry.kitName,
        reason,
        edits: [edit],
      });
    }
  }

  // ── Sirens and lights: number-bound, so a move can require a cascade. ──────

  for (const [kind, entries, pool] of [
    ["siren", index.sirens, ssla ? "sirenSsla" : "siren"],
    ["light", index.lights, "light"],
  ] as const) {
    const groups = groupById(entries);
    const taken = new Set(entries.map((entry) => entry.id));

    // First pass: decide movers and survivors per id, before allocating anything,
    // so an ambiguous group can be refused without spending an id on it.
    const decided: {
      id: number;
      movers: Mover<CarcolsProfile>[];
      survivors: number;
      refs: CarcolsVariationRef[];
    }[] = [];

    for (const [id, group] of groups) {
      const movers = moversFor(group, kind, ssla);
      if (movers.length === 0) continue;
      const survivors = group.length - movers.length;
      const refs = index.variationRefs.filter((ref) => ref.kind === kind && ref.id === id);
      decided.push({ id, movers, survivors, refs });
    }

    const totalMovers = decided.reduce((sum, d) => sum + d.movers.length, 0);
    const newIds = allocateIds(pool, Math.min(totalMovers, MAX_RENUMBER_BATCH), taken);
    let cursor = 0;

    for (const { id, movers, survivors, refs } of decided) {
      const soleMoverAway = movers.length === 1 && survivors === 0;
      const groupMovesAwayAmbiguously = movers.length > 1 && survivors === 0;

      // Checked before anything else, and regardless of how many references this
      // scan actually found: a resource with an unreadable carvariations.meta
      // might hold more of them, and "zero found" is not the same claim as "zero
      // exist" when a file that could have held some was never read at all.
      //
      // Any resource in the scan, not just the mover's own. A siren or light id
      // is global — the reference that makes this move unsafe is routinely in
      // somebody else's resource, which is why `refs` above searches all of them
      // — so an unreadable carvariations.meta anywhere is an unreadable file
      // that could hold one.
      if (unverifiableCarvariations.length > 0) {
        const named = unverifiableCarvariations.join(", ");
        for (const mover of movers) {
          refused.push({
            pool,
            resource: mover.entry.resource,
            id,
            reason:
              `${refs.length} reference${refs.length === 1 ? "" : "s"} to ${kind} id ${id} were ` +
              `found in this scan, but ${named} ${unverifiableCarvariations.length === 1 ? "has" : "have"} ` +
              "a carvariations.meta this scan could not read as XML — and a siren or light id is a " +
              "global number, so a reference to this one could be sitting in it. Renumbering would " +
              "risk leaving an unknown number of vehicles pointed at the old id, so this move was " +
              "refused. Fix that file's XML and scan again.",
          });
        }
        continue;
      }

      if (groupMovesAwayAmbiguously && refs.length > 0) {
        for (const mover of movers) {
          refused.push({
            pool,
            resource: mover.entry.resource,
            id,
            reason:
              `${refs.length} reference${refs.length === 1 ? "" : "s"} in carvariations.meta ` +
              `point at ${kind} id ${id}, and ${movers.length} definitions in this scan all have ` +
              "to move away from it at once. Nothing says which vehicle meant which definition, " +
              "so none of them were renumbered — resolve the collision by hand, or remove one " +
              "definition, and re-run this scan.",
          });
        }
        continue;
      }

      const needsCascade = soleMoverAway && refs.length > 0;

      for (const { entry, reason } of movers) {
        if (cursor >= newIds.length) {
          refused.push({
            pool,
            resource: entry.resource,
            id,
            reason:
              `The ${kind} id pool has no more safe ids left to hand out, so this id was left ` +
              "unchanged.",
          });
          continue;
        }
        const to = newIds[cursor];
        cursor += 1;

        const defEdit = definitionEdit(entry.resource, entry.file, entry.span, entry.raw, to);
        if (!defEdit) {
          refused.push({
            pool,
            resource: entry.resource,
            id,
            reason: `The exact text of this id could not be relocated in ${entry.file}.`,
          });
          continue;
        }

        const edits = [defEdit];
        if (needsCascade) {
          let cascadeFailed = false;
          for (const ref of refs) {
            const refEdit = referenceEdit(ref, to);
            if (!refEdit) {
              cascadeFailed = true;
              break;
            }
            edits.push(refEdit);
          }
          if (cascadeFailed) {
            refused.push({
              pool,
              resource: entry.resource,
              id,
              reason:
                `A reference to ${kind} id ${id} in carvariations.meta could not be relocated ` +
                "in its file, so this id was left unchanged rather than moving the definition " +
                "and leaving that reference dangling.",
            });
            continue;
          }
        }

        moves.push({ pool, resource: entry.resource, from: id, to, reason, edits });
      }
    }
  }

  return { moves, refused };
}

/** One resource's one file, with every edit any accepted move needs in it. */
export type RenumberFileEdits = {
  /** `GtaResourceInput.identity ?? name` — which resource's `texts` to look `file` up in. */
  resource: string;
  /** Resource-relative path, exactly as `GtaResourceInput.texts` keys it. */
  file: string;
  edits: GtaTextEdit[];
};

/**
 * Every move's edits, grouped by the *resource and* file they belong to, ready
 * for `applyEdits` one file at a time.
 *
 * Keyed on both halves rather than on the path alone, because the path alone is
 * not unique across a drop and this is the exact grouping that has to be right:
 * `carvariations.meta` is what nearly every resource calls its variations file,
 * so a plan spanning two resources produced two different files under one key.
 * A caller then applied both resources' edits to whichever text it happened to
 * fetch, `applyEdits` rejected the foreign ones on the text mismatch, and a
 * caller that skips a file where nothing applied never saw the rejection.
 *
 * The `Map` key is an implementation detail — read `resource` and `file` off the
 * value, never parse the key.
 */
export function groupEditsByFile(
  moves: readonly RenumberMove[],
): Map<string, RenumberFileEdits> {
  const byFile = new Map<string, RenumberFileEdits>();
  for (const move of moves) {
    for (const { resource, edit } of move.edits) {
      const file = pathKey(edit.file);
      const key = `${normalizeName(resource)} ${file}`;
      const held = byFile.get(key);
      if (held) held.edits.push(edit);
      else byFile.set(key, { resource, file, edits: [edit] });
    }
  }
  return byFile;
}
